import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ShoppingCart, Scale } from 'lucide-react';
import { STORAGE_BASE } from '../config';
import { priceInfo } from '../lib/price';
import ImageFallback from '../components/ImageFallback';

export const LIST_SHADOWS = {
  none: 'none',
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};
export const SHADOW_OPTS = [
  { value: 'none', label: 'بدون سایه' },
  { value: 'sm', label: 'کم' },
  { value: 'md', label: 'متوسط' },
  { value: 'lg', label: 'زیاد' },
  { value: 'xl', label: 'خیلی زیاد' },
];

const num = (v, d) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

export function defaultProductTpl() {
  return {
    layout: 'scroll', columns: 4, columnsMobile: 2, gap: 16,
    cardWidth: 250, cardMinHeight: 0,
    image: 'top', imageHeight: 190, imageFixedHeight: true, sideWidth: 130,
    bg: 'var(--card)', borderWidth: 0, borderColor: 'var(--border)', radius: 14, shadow: 'md',
    padding: 12, textAlign: 'right',
    titleSize: 15, titleColor: 'var(--card-fg)', titleLines: 2,
    showDesc: true, descLines: 2, descColor: 'var(--muted-fg)', descSize: 12,
    showPrice: true, priceSize: 14, priceColor: 'var(--accent)',
    showStock: true, showButton: false, buttonLabel: 'مشاهده', buttonStyle: 'outline',
    showNav: true,
  };
}

export function defaultPostTpl() {
  return {
    layout: 'grid', columns: 3, columnsMobile: 1, gap: 16,
    cardWidth: 280, cardMinHeight: 0,
    image: 'top', imageHeight: 170, imageFixedHeight: true, sideWidth: 140,
    bg: 'var(--card)', borderWidth: 1, borderColor: 'var(--border)', radius: 16, shadow: 'sm',
    padding: 14, textAlign: 'right',
    titleSize: 15, titleColor: 'var(--card-fg)', titleLines: 2,
    showDesc: true, descLines: 2, descColor: 'var(--muted-fg)', descSize: 13,
    showDate: true, showButton: false, buttonLabel: 'ادامه مطلب', buttonStyle: 'outline',
    showNav: true,
  };
}

export const blankTpl = kind => (kind === 'post' ? defaultPostTpl() : defaultProductTpl());

/* ---------- template store (public /site-settings, cached) ---------- */
let storeCache = null;
let storePromise = null;
export function fetchListTemplates() {
  if (storeCache) return Promise.resolve(storeCache);
  if (storePromise) return storePromise;
  storePromise = axios.get('/site-settings').then(r => {
    const lt = r.data?.list_templates || {};
    storeCache = { product: lt.product || [], post: lt.post || [] };
    return storeCache;
  }).catch(() => ({ product: [], post: [] }));
  return storePromise;
}
export function useListTplStore() {
  const [store, setStore] = useState(storeCache || { product: [], post: [] });
  useEffect(() => { fetchListTemplates().then(setStore); }, []);
  return store;
}
export const useVw = () => {
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const on = () => setVw(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vw;
};

/** Merge widget settings + named template + legacy fields into one tpl object. */
export function resolveTpl(settings = {}, kind = 'product', store = null) {
  const tpl = blankTpl(kind);
  if (settings.templateId && settings.templateId !== 'custom' && store) {
    const found = (store[kind] || []).find(t => String(t.id) === String(settings.templateId));
    if (found && found.tpl) Object.assign(tpl, found.tpl);
  }
  if (settings.tpl && typeof settings.tpl === 'object') Object.assign(tpl, settings.tpl);
  // Legacy widgets: honour old top-level fields when no template is involved
  if (!settings.templateId && !settings.tpl) {
    if (settings.columns != null) tpl.columns = settings.columns;
    if (settings.showCover === false && kind === 'post') tpl.image = 'none';
    if (settings.showExcerpt === false && kind === 'post') tpl.showDesc = false;
  }
  return tpl;
}

const clamp = (text, lines) => ({
  overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical',
});

function CardShell({ tpl, children, layout }) {
  const bw = tpl.borderWidth === undefined ? 1 : num(tpl.borderWidth, 0);
  const bwCss = tpl.borderWidth === undefined ? 'var(--shape-line)' : `${bw}px`;
  const border = bw > 0 ? `${bwCss} solid ${tpl.borderColor || 'var(--border)'}` : 'none';
  return (
    <div className="pcard-shell" style={{
      background: tpl.bg || 'var(--card)', border, borderRadius: tpl.radius ? `${tpl.radius}px` : 'var(--shape-card)',
      overflow: 'hidden', boxShadow: LIST_SHADOWS[tpl.shadow] || 'none',
      display: 'flex', flexDirection: 'column', height: '100%',
      minHeight: num(tpl.cardMinHeight, 0) || undefined, textAlign: tpl.textAlign || 'right',
    }}>
      {children}
    </div>
  );
}

function Img({ src, alt, h, fixedHeight, fit = 'cover' }) {
  const [err, setErr] = useState(false);
  if (err) return <div style={{ aspectRatio: '4 / 3', background: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted-fg)', fontSize: '0.75rem' }}>تصویر بارگذاری نشد</div>;
  return (
    <div className="pcard-media" style={{ aspectRatio: '4 / 3', flexShrink: 0, overflow: 'hidden', background: 'var(--muted)' }}>
      <img src={src} alt={alt} loading="lazy" onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
  );
}

function TplButton({ tpl }) {
  if (!tpl.showButton) return null;
  const solid = (tpl.buttonStyle || 'outline') === 'solid';
  return (
    <span style={{
      display: 'inline-block', marginTop: 10, padding: '8px 22px', borderRadius: 'var(--shape-btn)',
      fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', alignSelf: tpl.textAlign === 'center' ? 'center' : 'flex-start',
      background: solid ? 'var(--primary)' : 'transparent', color: solid ? 'var(--on-primary)' : 'var(--primary)',
      border: solid ? 'none' : '1.5px solid var(--primary)',
    }}>{tpl.buttonLabel || 'مشاهده'}</span>
  );
}

export function ProductCard({ p, tpl }) {
  const src = p.image_path || p.media?.[0]?.path || p.customization_data?.image
    ? `${STORAGE_BASE}/${String(p.image_path || p.media?.[0]?.path || p.customization_data?.image || '').replace(/^\/+/, '')}`
    : '';
  // بدون عکس: کاشی متنی پیش‌فرض (تا عکس واقعی یا خروجی عکس‌ساز بیاید)
  const ImgOrTile = ({ style }) => src
    ? <ImgOrTile style={style} />
    : <ImageFallback label={p.name} seed={String(p.id || p.name)} style={style} />;
  const pi = priceInfo(p);
  const price = pi.price;
  const out = (p.stock ?? 0) < 1;
  const [adding, setAdding] = useState(null); // 'cart' | 'compare' | null
  const authH = () => ({ headers: { Authorization: `Bearer ${(() => { try { return localStorage.getItem('customer_token') || ''; } catch { return ''; } })()}` } });
  const needLogin = () => {
    try { if (localStorage.getItem('customer_token')) return false; } catch {}
    toast.info('برای افزودن ابتدا وارد شوید.');
    window.dispatchEvent(new CustomEvent('open-login'));
    return true;
  };
  const quickAdd = async e => {
    e.preventDefault(); e.stopPropagation();
    if (adding) return;
    if (needLogin()) return;
    setAdding('cart');
    try {
      await axios.post('/cart/add', { product_id: p.id, qty: 1 }, authH());
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('به سبد خرید اضافه شد.');
    } catch (err) { toast.error(err.response?.data?.message || 'خطا در افزودن به سبد.'); }
    setAdding(null);
  };
  const quickCompare = async e => {
    e.preventDefault(); e.stopPropagation();
    if (adding) return;
    if (needLogin()) return;
    setAdding('compare');
    try {
      await axios.post('/compare/add', { product_id: p.id }, authH());
      window.dispatchEvent(new Event('compare-updated'));
      toast.success('به مقایسه اضافه شد.');
    } catch (err) { toast.error(err.response?.data?.message || 'خطا در افزودن به مقایسه.'); }
    setAdding(null);
  };
  const spin = <span style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', display: 'inline-block', animation: 'hb-spin 0.6s linear infinite' }} />;
  const iconBtn = {
    width: 34, height: 34, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: 'var(--shape-line) solid var(--border)', background: 'var(--card)', color: 'var(--primary)', cursor: 'pointer', fontSize: '1rem',
  };
  const body = (
    <div style={{ padding: num(tpl.padding, 12), display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ fontWeight: 700, fontSize: num(tpl.titleSize, 15), color: tpl.titleColor || 'var(--card-fg)', lineHeight: 1.6, minHeight: num(tpl.titleSize, 15) * 1.6 * num(tpl.titleLines, 2), ...clamp(null, num(tpl.titleLines, 2)) }}>{p.name}</div>
      {tpl.showDesc !== false && (
        <div style={{ fontSize: num(tpl.descSize, 12), color: tpl.descColor || 'var(--muted-fg)', lineHeight: 1.9, marginTop: 4, minHeight: num(tpl.descSize, 12) * 1.9 * num(tpl.descLines, 2), ...clamp(null, num(tpl.descLines, 2)) }}>{p.description || ''}</div>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {tpl.showPrice !== false && (
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            {pi.hasDiscount && <span style={{ background: 'var(--primary)', color: 'var(--on-primary)', fontSize: '0.66rem', fontWeight: 800, borderRadius: 'var(--shape-btn)', padding: '2px 8px' }}>٪{pi.pct.toLocaleString('fa-IR')}</span>}
            {(p.customization_data?.ar_model || p.customization_data?.ar_image) && <span style={{ background: 'var(--accent)', color: 'var(--on-primary)', fontSize: '0.66rem', fontWeight: 800, borderRadius: 'var(--shape-btn)', padding: '2px 8px' }}>پرو AR</span>}
            <span style={{ color: tpl.priceColor || 'var(--accent)', fontWeight: 800, fontSize: num(tpl.priceSize, 14) }}>
              از {Number(price).toLocaleString('fa-IR')} تومان
            </span>
            {pi.hasDiscount && <span style={{ color: 'var(--muted-fg)', fontSize: '0.72rem', textDecoration: 'line-through' }}>{Number(pi.base).toLocaleString('fa-IR')}</span>}
          </span>
        )}
        <span style={{ flexGrow: 1 }} />
        {tpl.showCart !== false && !out && (
          <button onClick={quickAdd} disabled={!!adding} title="افزودن به سبد خرید" aria-label="افزودن به سبد خرید" style={{ ...iconBtn, opacity: adding ? 0.6 : 1, cursor: adding ? 'wait' : 'pointer' }}>{adding === 'cart' ? spin : <ShoppingCart size={17} />}</button>
        )}
        {tpl.showCompare !== false && (
          <button onClick={quickCompare} disabled={!!adding} title="مقایسه" aria-label="مقایسه" style={{ ...iconBtn, opacity: adding ? 0.6 : 1, cursor: adding ? 'wait' : 'pointer' }}>{adding === 'compare' ? spin : <Scale size={17} />}</button>
        )}
        {tpl.showStock !== false && out && (
          <span style={{ fontSize: '0.7rem', fontWeight: 800, background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--shape-btn)', padding: '3px 10px' }}>ناموجود</span>
        )}
      </div>
      <TplButton tpl={tpl} />
    </div>
  );
  const ih = tpl.imageFixedHeight ? num(tpl.imageHeight, 190) : num(tpl.imageHeight, 190);
  let inner;
  if (tpl.image === 'none') inner = body;
  else if (tpl.image === 'background') inner = (
    <div style={{ position: 'relative', minHeight: Math.max(ih, 220), display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <ImgOrTile style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <div style={{ position: 'relative', padding: num(tpl.padding, 12), color: '#fff' }}>
        <div style={{ fontWeight: 800, fontSize: num(tpl.titleSize, 15), lineHeight: 1.6, ...clamp(null, num(tpl.titleLines, 2)) }}>{p.name}</div>
        {tpl.showPrice !== false && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {pi.hasDiscount && <span style={{ background: 'var(--primary)', color: 'var(--on-primary)', fontSize: '0.66rem', fontWeight: 800, borderRadius: 'var(--shape-btn)', padding: '2px 8px' }}>٪{pi.pct.toLocaleString('fa-IR')}</span>}
            {(p.customization_data?.ar_model || p.customization_data?.ar_image) && <span style={{ background: 'var(--accent)', color: '#fff', fontSize: '0.66rem', fontWeight: 800, borderRadius: 'var(--shape-btn)', padding: '2px 8px' }}>پرو AR</span>}
            <span style={{ fontWeight: 800, fontSize: num(tpl.priceSize, 14) }}>از {Number(price).toLocaleString('fa-IR')} تومان</span>
            {pi.hasDiscount && <span style={{ opacity: 0.8, fontSize: '0.72rem', textDecoration: 'line-through' }}>{Number(pi.base).toLocaleString('fa-IR')}</span>}
          </div>
        )}
        <TplButton tpl={{ ...tpl, buttonStyle: 'solid', buttonLabel: tpl.buttonLabel }} />
      </div>
    </div>
  );
  else if (tpl.image === 'side') inner = (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
      <div style={{ width: num(tpl.sideWidth, 130), flexShrink: 0, background: 'var(--muted)' }}>
        <ImgOrTile style={{ width: '100%', height: '100%', minHeight: 120, objectFit: 'cover', display: 'block' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
    </div>
  );
  else inner = (<><Img src={src} alt={p.name} h={ih} fixedHeight={!!tpl.imageFixedHeight} />{body}</>);
  return (
    <Link to={`/products/${p.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
      <CardShell tpl={tpl} layout={tpl.layout}>{inner}</CardShell>
    </Link>
  );
}

export function PostCard({ p, tpl }) {
  const cover = p.cover ? (/^https?:\/\//.test(p.cover) ? p.cover : `${STORAGE_BASE}/${String(p.cover).replace(/^\/+/, '')}`) : null;
  const body = (
    <div style={{ padding: num(tpl.padding, 14), display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ fontWeight: 800, fontSize: num(tpl.titleSize, 15), color: tpl.titleColor || 'var(--card-fg)', lineHeight: 1.7, ...clamp(null, num(tpl.titleLines, 2)) }}>{p.title}</div>
      {tpl.showDesc !== false && p.excerpt && (
        <div style={{ fontSize: num(tpl.descSize, 13), color: tpl.descColor || 'var(--muted-fg)', lineHeight: 1.9, marginTop: 4, ...clamp(null, num(tpl.descLines, 2)) }}>{p.excerpt}</div>
      )}
      {tpl.showDate !== false && p.published_at && (
        <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)', marginTop: 8 }}>{new Date(p.published_at).toLocaleDateString('fa-IR')}</div>
      )}
      <TplButton tpl={tpl} />
    </div>
  );
  const ih = num(tpl.imageHeight, 170);
  let inner;
  if (tpl.image === 'none' || !cover) inner = body;
  else if (tpl.image === 'background') inner = (
    <div style={{ position: 'relative', minHeight: Math.max(ih, 220), display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <img src={cover} alt={p.title} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <div style={{ position: 'relative', padding: num(tpl.padding, 14), color: '#fff' }}>
        <div style={{ fontWeight: 800, fontSize: num(tpl.titleSize, 15), lineHeight: 1.7, ...clamp(null, num(tpl.titleLines, 2)) }}>{p.title}</div>
        <TplButton tpl={{ ...tpl, buttonStyle: 'solid', buttonLabel: tpl.buttonLabel }} />
      </div>
    </div>
  );
  else if (tpl.image === 'side') inner = (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
      <div style={{ width: num(tpl.sideWidth, 140), flexShrink: 0, background: 'var(--muted)' }}>
        <img src={cover} alt={p.title} loading="lazy" style={{ width: '100%', height: '100%', minHeight: 120, objectFit: 'cover', display: 'block' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
    </div>
  );
  else inner = (<><Img src={cover} alt={p.title} h={ih} />{body}</>);
  return (
    <a href={`/blog/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
      <CardShell tpl={tpl} layout={tpl.layout}>{inner}</CardShell>
    </a>
  );
}

/**
 * List using the admin-marked default template for the kind
 * (falls back to built-in design when no default exists).
 */
export function DefaultTemplateList({ kind = 'product', items = [], settings = {}, fallbackTpl = null }) {
  const store = useListTplStore();
  const def = (store[kind] || []).find(t => t.isDefault) || null;
  const merged = { ...settings };
  if (!merged.templateId && def) merged.templateId = def.id;
  if (!merged.templateId && fallbackTpl) merged.tpl = { ...fallbackTpl, ...(merged.tpl || {}) };
  return <TemplateList kind={kind} items={items} settings={merged} />;
}

/**
 * Full list section driven by a template.
 * kind: 'product' | 'post'. settings: widget settings (sectionTitle, templateId, tpl, ...).
 */
export default function TemplateList({ kind = 'product', items = [], settings = {} }) {
  const store = useListTplStore();
  const vw = useVw();
  const trackRef = useRef(null);
  const tpl = resolveTpl(settings, kind, store);
  const mobile = vw < 768;
  if (!items || !items.length) return null;

  const layout = tpl.layout || 'grid';
  const cols = mobile ? Math.min(num(tpl.columnsMobile, layout === 'grid' ? 2 : 2), 3) : num(tpl.columns, 4);
  const gap = num(tpl.gap, 16);
  const Card = kind === 'post' ? PostCard : ProductCard;
  const scrollBy = d => trackRef.current && trackRef.current.scrollBy({ left: d * (num(tpl.cardWidth, 250) + gap), behavior: 'smooth' });
  const navBtn = { background: 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: '1.1em', color: 'var(--primary)', flexShrink: 0 };

  let listEl;
  if (layout === 'scroll') {
    listEl = (
      <div ref={trackRef} style={{ display: 'flex', gap, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 10, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {items.map(p => (
          <div key={p.id} style={{ flex: `0 0 ${num(tpl.cardWidth, 250)}px`, scrollSnapAlign: 'start' }}>
            <Card p={p} tpl={{ ...tpl, layout: 'scroll' }} />
          </div>
        ))}
      </div>
    );
  } else if (layout === 'list') {
    listEl = (
      <div style={{ display: 'flex', flexDirection: 'column', gap }}>
        {items.map(p => <Card key={p.id} p={p} tpl={{ ...tpl, layout: 'list', image: tpl.image === 'top' ? 'side' : tpl.image }} />)}
      </div>
    );
  } else {
    listEl = (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
        {items.map(p => <Card key={p.id} p={p} tpl={{ ...tpl, layout: 'grid' }} />)}
      </div>
    );
  }

  return (
    <div>
      {(settings.sectionTitle || (layout === 'scroll' && tpl.showNav !== false)) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: settings.titleSize ? `${settings.titleSize}px` : 'clamp(1.25rem, 3vw, 1.6rem)', color: settings.titleColor || 'inherit' }}>
            {settings.sectionTitle || settings.title || ''}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {kind === 'product' && <a href="/products" className="text-sm text-muted-foreground hover:text-foreground">دیدن همه</a>}
            {layout === 'scroll' && tpl.showNav !== false && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => scrollBy(-1)} aria-label="قبلی" style={navBtn}>→</button>
                <button onClick={() => scrollBy(1)} aria-label="بعدی" style={navBtn}>←</button>
              </div>
            )}
          </div>
        </div>
      )}
      {listEl}
    </div>
  );
}
